import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ImageCategory from './ImageCategory';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Corrected import for DeleteIcon
import DownloadIcon from '@mui/icons-material/Download';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close'; // Using CloseIcon for the navbar toggle

import { Helmet } from 'react-helmet';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

// --- NEW: Skeleton Loader Component ---
const SkeletonCard = () => (
  <div className="gallery-card skeleton">
    <div className="skeleton-image"></div>
    <div className="card-info">
      <div className="skeleton-title"></div>
      <div className="skeleton-description"></div>
      <div className="skeleton-chips">
        <span className="skeleton-chip"></span>
        <span className="skeleton-chip"></span>
        <span className="skeleton-chip"></span>
      </div>
      <div className="action-buttons icon-buttons">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);
// --- END NEW: Skeleton Loader Component ---


const Images = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImageIndex, setPreviewImageIndex] = useState(null);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dynamicImagesPerPage, setDynamicImagesPerPage] = useState(10);

  const [categorySearchModal, setCategorySearchModal] = useState('');
  const [batchCategorySearch, setBatchCategorySearch] = useState('');

  const optionsDropdownRef = useRef(null);
  const editModalRef = useRef(null); // Ref for the edit/upload modal
  const batchModalRef = useRef(null); // Ref for the batch upload modal
  const trashModalRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    tags: '',
    photo: null,
    imageCategories: [],
  });

  const [batchFiles, setBatchFiles] = useState([]);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchTags, setBatchTags] = useState('');
  const [batchCategory, setBatchCategory] = useState([]);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadingIndex, setUploadingIndex] = useState(0);
  const [uploadingTotal, setUploadingTotal] = useState(0);

  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [queuedForDeletion, setQueuedForDeletion] = useState([]);
  const [isProcessingDeletionQueue, setIsProcessingDeletionQueue] = useState(false);

  const [showTrashModal, setShowTrashModal] = useState(false);

  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 800);

  const [isLoading, setIsLoading] = useState(false);

  // State to manage download button cooldown
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);

  // NEW: State for skeleton loader specifically for images
  const [areImagesLoading, setAreImagesLoading] = useState(true);


  const fetchImages = useCallback(async () => {
    setAreImagesLoading(true); // Start image loading
    try {
      const res = await axios.get(`${API_BASE_URL}/api/images`);
      setImages(res.data || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      toast.error('Error fetching images.');
    } finally {
      setAreImagesLoading(false); // End image loading
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
      // Sort categories alphabetically by name
      const sortedCategories = res.data ? [...res.data].sort((a, b) => a.name.localeCompare(b.name)) : [];
      setCategories(sortedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Error fetching categories.');
    }
  }, []);

  const getOrCreateScreenshotsCategory = useCallback(async () => {
    try {
      const existingCategory = categories.find(cat => cat.name.toLowerCase() === 'screenshots');
      if (existingCategory) {
        return existingCategory._id;
      } else {
        const res = await axios.post(`${API_BASE_URL}/api/image-categories`, { name: 'Screenshots' });
        toast.success('🚀 "Screenshots" category created!');
        fetchCategories(); // Re-fetch to get sorted list including new category
        return res.data._id;
      }
    } catch (err) {
      console.error('Error getting or creating "Screenshots" category:', err);
      toast.error('Error with "Screenshots" category.');
      return null;
    }
  }, [categories, fetchCategories]);

  const handleSubmit = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    setIsLoading(true);
    try {
      setUploadProgress(0);
      setUploadingCount(1);

      if (editingImage?._id) {
        await axios.patch(`${API_BASE_URL}/api/images/${editingImage._id}`, {
          name: form.name,
          description: form.description,
          tags: form.tags.split(',').map((t) => t.trim()),
          imageCategories: form.imageCategories,
        });
        toast.success('✅ Image updated');
      } else {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('tags', JSON.stringify(form.tags.split(',').map((t) => t.trim())));
        formData.append('imageCategories', JSON.stringify(form.imageCategories));
        formData.append('photo', form.photo);

        await axios.post(`${API_BASE_URL}/api/images`, formData, {
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(percent);
          },
        });
        toast.success('📤 Image uploaded');
      }

      setEditingImage(null);
      setForm({
        name: '',
        description: '',
        tags: '',
        photo: null,
        imageCategories: [],
      });
      setCategorySearchModal('');
      fetchImages();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('❌ Upload failed');
    } finally {
      setUploadProgress(0);
      setUploadingCount(0);
      setIsLoading(false);
    }
  }, [editingImage, form, fetchImages]);

  const handleBatchUpload = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const files = Array.from(batchFiles);
    if (files.length === 0) return;

    const categoriesToAssign = batchCategory;

    setUploadingTotal(files.length);
    setUploadingIndex(0);
    setUploadProgress(0);
    setIsLoading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadingIndex(i + 1);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('name', file.name);
        formData.append('description', '');
        formData.append('tags', JSON.stringify(batchTags.split(',').map((t) => t.trim())));
        formData.append('imageCategories', JSON.stringify(categoriesToAssign));
        formData.append('photo', file);

        await axios.post(`${API_BASE_URL}/api/images`, formData, {
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(percent);
          },
        });
      }

      toast.success('📤 Batch upload complete');
      setBatchFiles([]);
      setBatchTags('');
      setBatchCategory([]);
      setIsBatchModalOpen(false);
      setBatchCategorySearch('');
      fetchImages();
    } catch (err) {
      console.error('Batch upload error:', err);
      toast.error('❌ Batch upload failed');
    } finally {
      setUploadingIndex(0);
      setUploadingTotal(0);
      setUploadProgress(0);
      setIsLoading(false);
    }
  }, [batchFiles, batchTags, batchCategory, fetchImages]);

  const queueImageForDeletion = useCallback((imageIdsToQueue) => {
    const ids = Array.isArray(imageIdsToQueue) ? imageIdsToQueue : [imageIdsToQueue];

    setQueuedForDeletion(prevQueue => {
      const newQueue = [...prevQueue];
      let addedCount = 0;
      ids.forEach(id => {
        if (!newQueue.includes(id)) {
          newQueue.push(id);
          addedCount++;
        }
      });
      if (addedCount > 0) {
        toast.info(`🗑️ ${addedCount} item(s) moved to trash`);
      }
      return newQueue;
    });

    if (previewImage && ids.includes(previewImage._id)) {
      setPreviewImage(null);
      setPreviewImageIndex(null);
      setShowOptionsDropdown(false);
    }
    setSelectedImageIds(prevSelected => prevSelected.filter(id => !ids.includes(id)));

  }, [previewImage]);

  const handleCancelDeletionQueue = useCallback(() => {
    setQueuedForDeletion([]);
    toast.info('Deletion queue cleared.');
  }, []);

  const handleExecuteQueuedDeletion = useCallback(async () => {
    if (queuedForDeletion.length === 0) return;

    setIsProcessingDeletionQueue(true);
    setIsLoading(true);
    const idsToDelete = [...queuedForDeletion];

    for (const imageId of idsToDelete) {
      try {
        await axios.delete(`${API_BASE_URL}/api/images/${imageId}`);
        toast.success(`🗑️ Image deleted permanently.`);

        setImages(prevImages => prevImages.filter(img => img._id !== imageId));
        setQueuedForDeletion(prevQueue => prevQueue.filter(id => id !== imageId));

      } catch (err) {
        console.error(`Error deleting image ${imageId}:`, err);
        toast.error(`❌ Failed to delete image ${imageId}.`);
      }
    }
    setIsProcessingDeletionQueue(false);
    setIsLoading(false);
    toast.success('Permanent deletion process complete!');
  }, [queuedForDeletion]);

  const handleDelete = useCallback((img) => {
    queueImageForDeletion(img._id);
  }, [queueImageForDeletion]);

  const handleBatchDeleteClick = useCallback(() => {
    if (selectedImageIds.length > 0) {
      queueImageForDeletion(selectedImageIds);
    } else {
      toast.info('Please select images to delete.');
    }
  }, [selectedImageIds, queueImageForDeletion]);

  const toggleImageSelection = useCallback((imageId) => {
    setSelectedImageIds((prevSelected) =>
      prevSelected.includes(imageId)
        ? prevSelected.filter((id) => id !== imageId)
        : [...prevSelected, imageId]
    );
  }, []);

  const handleRestoreImage = useCallback((imageId) => {
    setQueuedForDeletion(prevQueue => prevQueue.filter(id => id !== imageId));
    toast.success('Image restored!');
  }, []);

  const handlePermanentlyDeleteImage = useCallback(async (imageId) => {
    setIsLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/images/${imageId}`);
      setImages(prevImages => prevImages.filter(img => img._id !== imageId));
      setQueuedForDeletion(prevQueue => prevQueue.filter(id => id !== imageId));
      toast.success('Image permanently deleted.');
    } catch (err) {
      console.error('Error permanently deleting image:', err);
      toast.error('Failed to permanently delete image.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRestoreAll = useCallback(() => {
    setQueuedForDeletion([]);
    toast.success('All images restored from trash.');
  }, []);

  const handleEmptyTrash = useCallback(async () => {
    if (queuedForDeletion.length === 0) {
      toast.info('Trash is already empty.');
      return;
    }

    setIsProcessingDeletionQueue(true);
    setIsLoading(true);
    const idsToEmpty = [...queuedForDeletion];

    for (const imageId of idsToEmpty) {
      try {
        await axios.delete(`${API_BASE_URL}/api/images/${imageId}`);
        setImages(prevImages => prevImages.filter(img => img._id !== imageId));
        setQueuedForDeletion(prevQueue => prevQueue.filter(id => id !== imageId));

      } catch (err) {
        console.error(`Error emptying image ${imageId}:`, err);
        toast.error(`Failed to empty image ${imageId}.`);
      }
    }
    setIsProcessingDeletionQueue(false);
    setIsLoading(false);
    toast.success('Trash emptied permanently!');
  }, [queuedForDeletion]);

  // Refactored handleDownload to be simple and robust
  const handleDownload = useCallback(async (event, imageUrl, fileName) => {
    event.preventDefault(); // Crucial: Prevent default link behavior immediately

    // Exit early if already disabled (extra safeguard)
    if (isDownloadDisabled) {
      return;
    }

    setIsDownloadDisabled(true); // Disable the button immediately

    try {
      const response = await axios({
        url: imageUrl,
        method: 'GET',
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'download.png');
      document.body.appendChild(link);
      link.click(); // Programmatically click the link to initiate download
      link.parentNode.removeChild(link); // Clean up the temporary link
      window.URL.revokeObjectURL(url); // Clean up the blob URL

      toast.success(`⬇️ "${fileName || 'Image'}" downloaded!`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('❌ Failed to download image.');
    } finally {
      // Re-enable button after 5 seconds, regardless of success/failure
      setTimeout(() => {
        setIsDownloadDisabled(false);
      }, 5000);
    }
  }, [isDownloadDisabled]); // isDownloadDisabled is a dependency because it's read and updated


  useEffect(() => {
    fetchImages();
    fetchCategories();
  }, [fetchImages, fetchCategories]);

  useEffect(() => {
    const updateImagesPerPage = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setDynamicImagesPerPage(20);
      } else if (width >= 992) {
        setDynamicImagesPerPage(16);
      } else if (width >= 768) {
        setDynamicImagesPerPage(12);
      } else {
        setDynamicImagesPerPage(8);
      }
    };

    updateImagesPerPage();
    window.addEventListener('resize', updateImagesPerPage);
    return () => {
      window.removeEventListener('resize', updateImagesPerPage);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 800);
      if (window.innerWidth >= 800) {
        setIsNavbarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, tagFilter, categoryFilter, dynamicImagesPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsDropdownRef.current && !optionsDropdownRef.current.contains(event.target)) {
        setShowOptionsDropdown(false);
      }
    };

    if (showOptionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsDropdown]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (previewImage) {
          setPreviewImage(null);
          setPreviewImageIndex(null);
          setShowOptionsDropdown(false);
        } else if (editingImage) {
          setEditingImage(null);
          setForm({
            name: '',
            description: '',
            tags: '',
            photo: null,
            imageCategories: [],
          });
          setCategorySearchModal('');
        } else if (isBatchModalOpen) {
          setIsBatchModalOpen(false);
          setBatchFiles([]);
          setBatchTags('');
          setBatchCategory([]);
          setBatchCategorySearch('');
        } else if (showTrashModal) {
          setShowTrashModal(false);
        } else if (queuedForDeletion.length > 0) {
          handleCancelDeletionQueue();
        } else if (categoryModalOpen) {
          setCategoryModalOpen(false);
          fetchImages();
          fetchCategories();
          setForm({
            name: '',
            description: '',
            tags: '',
            photo: null,
            imageCategories: [],
          });
        }
      }
    };

    const handleEnterKeySubmit = (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        if (event.target.tagName !== 'TEXTAREA') {
          // Check if the target is within the main edit modal
          if (editingImage && editModalRef.current && editModalRef.current.contains(event.target)) {
            event.preventDefault();
            handleSubmit(event);
          // Check if the target is within the batch upload modal
          } else if (isBatchModalOpen && batchModalRef.current && batchModalRef.current.contains(event.target)) {
            event.preventDefault();
            handleBatchUpload(event);
          }
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('keydown', handleEnterKeySubmit);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('keydown', handleEnterKeySubmit);
    };
    // Keep form and batchFiles here
  }, [previewImage, editingImage, isBatchModalOpen, showTrashModal, categoryModalOpen, queuedForDeletion.length, handleSubmit, handleBatchUpload, handleCancelDeletionQueue, fetchImages, fetchCategories, form, batchFiles]);

  useEffect(() => {
    const handlePaste = async (event) => {
      const isUploadModalOpenForNew = editingImage && !editingImage._id;
      const isNoModalOpenAndPhotoEmpty = !editingImage && !isBatchModalOpen && !previewImage && !categoryModalOpen && !form.photo && queuedForDeletion.length === 0 && !showTrashModal;

      if (!isUploadModalOpenForNew && !isNoModalOpenAndPhotoEmpty) {
        return;
      }

      const items = event.clipboardData?.items;
      if (!items) return;

      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();

          const existingImageNames = images.map(img => img.name);
          let nextScreenshotNumber = 1;
          while (existingImageNames.includes(`screenshot-${nextScreenshotNumber}`)) {
            nextScreenshotNumber++;
          }

          const fileName = `screenshot-${nextScreenshotNumber}.png`;
          const file = new File([blob], fileName, { type: blob.type });

          const screenshotsCategoryId = await getOrCreateScreenshotsCategory();
          const initialCategories = screenshotsCategoryId ? [screenshotsCategoryId] : [];

          if (!editingImage) {
            setEditingImage({});
          }

          setForm((prev) => ({
            ...prev,
            photo: file,
            name: fileName.split('.')[0],
            description: '',
            tags: '',
            imageCategories: initialCategories,
          }));

          toast.success('📸 Screenshot pasted!');
          event.preventDefault();

          setTimeout(() => {
            if (editModalRef.current) {
              const nameInput = editModalRef.current.querySelector('input[name="name"]');
              if (nameInput) {
                nameInput.focus();
              } else {
                const firstAvailableInput = editModalRef.current.querySelector('input, textarea, select, button');
                if (firstAvailableInput) {
                  firstAvailableInput.focus();
                }
              }
            }
          }, 0);


          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [editingImage, form.photo, images, getOrCreateScreenshotsCategory, isBatchModalOpen, previewImage, categoryModalOpen, queuedForDeletion.length, showTrashModal, isLoading]);


  const uniqueTags = [...new Set(images.flatMap((img) => img.tags || []).filter(Boolean))];

  const filteredImages = images.filter((img) => {
    if (queuedForDeletion.includes(img._id)) {
      return false;
    }

    const matchesSearch =
      img.name?.toLowerCase().includes(search.toLowerCase()) ||
      img.description?.toLowerCase().includes(search.toLowerCase());
    const matchesTag = !tagFilter || img.tags?.includes(tagFilter);
    const matchesCategory =
      !categoryFilter || img.imageCategories?.some((cat) => cat._id === categoryFilter);
    return matchesSearch && matchesTag && matchesCategory;
  });

  const totalPages = Math.ceil(filteredImages.length / dynamicImagesPerPage);
  const displayedImages = filteredImages.slice(
    (currentPage - 1) * dynamicImagesPerPage,
    currentPage * dynamicImagesPerPage
  );

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success('📋 URL copied!'))
      .catch(() => toast.error('❌ Copy failed'));
  };

  const openPreviewModal = (img) => {
    if (queuedForDeletion.includes(img._id)) return;
    setPreviewImage(img);
    const index = filteredImages.findIndex((item) => item._id === img._id);
    setPreviewImageIndex(index);
  };

  const navigateImage = (direction) => {
    if (previewImageIndex === null) return;

    let newIndex = previewImageIndex + direction;

    if (newIndex < 0) {
      newIndex = filteredImages.length - 1;
    } else if (newIndex >= filteredImages.length) {
      newIndex = 0;
    }

    let targetImage = filteredImages[newIndex];
    while (targetImage && queuedForDeletion.includes(targetImage._id)) {
      newIndex += direction;
      if (newIndex < 0) newIndex = filteredImages.length - 1;
      else if (newIndex >= filteredImages.length) newIndex = 0;
      if (newIndex === previewImageIndex) {
        setPreviewImage(null);
        setPreviewImageIndex(null);
        return;
      }
      targetImage = filteredImages[newIndex];
    }

    setPreviewImage(targetImage);
    setPreviewImageIndex(newIndex);
  };

  const handleEditFromPreview = () => {
    if (!previewImage) return;
    setEditingImage(previewImage);
    setForm({
      name: previewImage.name,
      description: previewImage.description,
      tags: previewImage.tags?.join(', ') || '',
      photo: null,
      imageCategories: previewImage.imageCategories?.map((cat) => cat._id) || [],
    });
    setPreviewImage(null);
    setPreviewImageIndex(null);
    setShowOptionsDropdown(false);
    setCategorySearchModal('');
  };

  const handleDeleteFromPreview = () => {
    if (!previewImage) return;
    handleDelete(previewImage);
    setPreviewImage(null);
    setPreviewImageIndex(null);
    setShowOptionsDropdown(false);
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearchModal.toLowerCase())
  );

  const filteredBatchCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(batchCategorySearch.toLowerCase())
  );

  const imagesInTrash = images.filter(img => queuedForDeletion.includes(img._id));


  return (
    <div className="images-wrapper">
      <Helmet>
              <title>Images</title>
              <meta name="description" content="Images" />
              <meta property="og:title" content="Images" />
      </Helmet>
      {isSmallScreen && (
        <button
          className="navbar-toggle-btn"
          onClick={() => setIsNavbarVisible(prev => !prev)}
          title={isNavbarVisible ? "Hide Navbar" : "Show Navbar"}
        >
          {isNavbarVisible ? <CloseIcon /> : <MenuIcon />}
        </button>
      )}

      <div className={`mini-navbar ${isNavbarVisible ? 'visible' : 'hidden'}`}>
        <div className="top-row">
          <button className="open-form-btn" onClick={() => {
            setEditingImage({});
            setSelectedImageIds([]);
            setForm({
              name: '',
              description: '',
              tags: '',
              photo: null,
              imageCategories: [],
            });
            setCategorySearchModal('');
            // FIX: Focus the first input of the upload modal when opened
            setTimeout(() => {
              if (editModalRef.current) {
                const firstInput = editModalRef.current.querySelector('input, textarea, select, button');
                if (firstInput) {
                  firstInput.focus();
                }
              }
            }, 0);
          }}>Upload</button>
          <button className="open-form-btn" onClick={() => {
            setIsBatchModalOpen(true);
            setSelectedImageIds([]);
            // FIX: Focus the first input of the batch upload modal when opened
            setTimeout(() => {
              if (batchModalRef.current) {
                const firstInput = batchModalRef.current.querySelector('input, textarea, select, button');
                if (firstInput) {
                  firstInput.focus();
                }
              }
            }, 0);
          }}>Batch Upload</button>
          <button className="open-form-btn" onClick={() => setCategoryModalOpen(true)}>Manage Categories</button>
          {selectedImageIds.length > 0 && (
            <button
              className="delete-selected-btn"
              onClick={handleBatchDeleteClick}
              disabled={isProcessingDeletionQueue}
            >
              {`Delete Selected (${selectedImageIds.length})`}
            </button>
          )}
          <button className="open-form-btn trash-btn" onClick={() => setShowTrashModal(true)}>
            Trash ({queuedForDeletion.length})
          </button>
          <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="tag-filter">
            <option value="">All Tags</option>
            {uniqueTags.map((tag) => (
              <option key={tag}>{tag}</option>
            ))}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="category-filter">
            <option value="">All Categories</option>
            {/* Render sorted categories */}
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div className="bottom-row">
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          )}
          <input
            type="text"
            placeholder="Search images..."
            className="search-bar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="image-gallery-grid">
        {areImagesLoading ? ( // Conditional rendering for skeleton loader
          Array.from({ length: dynamicImagesPerPage }).map((_, index) => (
            <SkeletonCard key={index} />
          ))
        ) : (
          displayedImages.map((img) => {
            const isQueued = queuedForDeletion.includes(img._id);
            const isDisabled = isQueued || isProcessingDeletionQueue;

            return (
              <div
                key={img._id}
                className={`gallery-card ${isQueued ? 'queued-for-deletion' : ''}`}
              >
                <div className="image-selection-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedImageIds.includes(img._id) || isQueued}
                    onChange={() => toggleImageSelection(img._id)}
                    disabled={isDisabled}
                  />
                </div>
                <div
                  className={`image-preview ${isDisabled ? 'disabled-click' : ''}`}
                  onClick={isDisabled ? null : () => openPreviewModal(img)}
                >
                  <img src={img.url} alt={img.name} />
                  {isQueued && <div className="queued-overlay">QUEUED FOR DELETION</div>}
                </div>
                <div className="card-info">
                  <h3 className="image-title">{img.name}</h3>
                  <p className="image-desc">{img.description}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {img.imageCategories?.map(cat => (
                      <span key={cat._id} className="category-chip">{cat.name}</span>
                    ))}
                    {img.tags && img.tags.length > 0 && img.tags.filter(Boolean).map(tag => (
                      <span key={tag} className="tag-chip">{tag}</span>
                    ))}
                  </div>
                  <div className="action-buttons icon-buttons">
                    <button onClick={() => copyToClipboard(img.url)} disabled={isDisabled} title="Copy URL">
                      <ContentCopyIcon />
                    </button>
                    {/* Gallery Card Download Button: Use onClick directly with combined disabled state */}
                    <button
                      onClick={(e) => handleDownload(e, img.url, img.name + '.png')}
                      disabled={isDisabled || isDownloadDisabled}
                      title="Download Image"
                    >
                      <DownloadIcon />
                    </button>
                    <button
                      onClick={() => {
                        setEditingImage(img);
                        setForm({
                          name: img.name,
                          description: img.description,
                          tags: img.tags?.join(', ') || '',
                          photo: null,
                          imageCategories: img.imageCategories?.map((cat) => cat._id) || [],
                        });
                        setCategorySearchModal('');
                      }}
                      disabled={isDisabled}
                      title="Edit Image"
                    >
                      <EditIcon />
                    </button>
                    <button className="delete" onClick={() => handleDelete(img)} disabled={isDisabled} title="Delete Image">
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* NEW: Bottom Pagination */}
      {totalPages > 1 && (
        <div className="bottom-pagination">
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      )}

      {/* Add/Edit Image Modal */}
      {editingImage && (
        <div className="modal-overlay">
          <div className="modal-content" ref={editModalRef}>
            <h2>{editingImage._id ? 'Edit Image' : 'Upload Image'}</h2>
            <form onSubmit={handleSubmit} className="image-form">
              <input type="text" name="name" placeholder="Image name" value={form.name} onChange={handleChange} required />
              <input type="text" name="description" placeholder="Description" value={form.description} onChange={handleChange} />
              <input type="text" name="tags" placeholder="Tags (comma separated)" value={form.tags} onChange={handleChange} />

              <label htmlFor="categorySearchModal">Search Categories:</label>
              <input
                type="search"
                id="categorySearchModal"
                className="category-search-input"
                placeholder="Search categories..."
                value={categorySearchModal}
                onChange={(e) => setCategorySearchModal(e.target.value)}
              />

              <label>Select Categories:</label>
              <div className="category-checkboxes"> {/* Max-height and overflow-y: auto applied via SCSS */}
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat._id}
                      className={`category-chip-selectable ${form.imageCategories.includes(cat._id) ? 'selected' : ''}`}
                      onClick={() => {
                        const updated = form.imageCategories.includes(cat._id)
                          ? form.imageCategories.filter((id) => id !== cat._id)
                          : [...form.imageCategories, cat._id];
                        setForm((prev) => ({
                          ...prev,
                          imageCategories: updated,
                        }));
                      }}
                    >
                      {cat.name}
                    </div>
                  ))
                ) : (
                  <p className="no-categories-message">No categories found.</p>
                )}
              </div>
              {!editingImage._id && (
                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleChange}
                  required={!form.photo}
                />
              )}
              {(form.photo || editingImage?.url) && (
                <div className="image-preview">
                  <img src={form.photo ? URL.createObjectURL(form.photo) : editingImage?.url} alt="Preview" />
                </div>
              )}
              <div className="form-buttons">
                <button type="submit">{editingImage._id ? 'Update' : 'Upload'}</button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setEditingImage(null);
                    setForm({
                      name: '',
                      description: '',
                      tags: '',
                      photo: null,
                      imageCategories: [],
                    });
                    setCategorySearchModal('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Upload Modal */}
      {isBatchModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" ref={batchModalRef}>
            <h2>Batch Upload</h2>
            <form onSubmit={handleBatchUpload} className="image-form">
              <input type="file" accept="image/*" multiple onChange={(e) => setBatchFiles(e.target.files)} required />
              <input type="text" placeholder="Tags (comma separated)" value={batchTags} onChange={(e) => setBatchTags(e.target.value)} />

              <label htmlFor="batchCategorySearch">Search Categories:</label>
              <input
                type="search"
                id="batchCategorySearch"
                className="category-search-input"
                placeholder="Search categories..."
                value={batchCategorySearch}
                onChange={(e) => setBatchCategorySearch(e.target.value)}
              />

              <label>Select Categories (for all batch files):</label>
              <div className="category-checkboxes"> {/* Max-height and overflow-y: auto applied via SCSS */}
                {filteredBatchCategories.length > 0 ? (
                  filteredBatchCategories.map((cat) => (
                    <div
                      key={cat._id}
                      className={`category-chip-selectable ${batchCategory.includes(cat._id) ? 'selected' : ''}`}
                      onClick={() => {
                        const updated = batchCategory.includes(cat._id)
                          ? batchCategory.filter((id) => id !== cat._id)
                          : [...batchCategory, cat._id];
                        setBatchCategory(updated);
                      }}
                    >
                      {cat.name}
                    </div>
                  ))
                ) : (
                  <p className="no-categories-message">No categories found.</p>
                )}
              </div>

              <div className="form-buttons">
                <button type="submit">Upload All</button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setBatchFiles([]);
                    setBatchTags('');
                    setBatchCategory([]);
                    setBatchCategorySearch('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {categoryModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <ImageCategory
              onClose={() => {
                setCategoryModalOpen(false);
                fetchImages();
                fetchCategories();
                setForm({
                  name: '',
                  description: '',
                  tags: '',
                  photo: null,
                  imageCategories: [],
                });
              }}
              showToast={toast}
              refreshCategories={fetchCategories}
              categories={categories}
            />
          </div>
        </div>
      )}

      {/* Trash Modal */}
      {showTrashModal && (
        <div className="modal-overlay">
          <div className="modal-content trash-modal" ref={trashModalRef}>
            <button className="close-btn" onClick={() => setShowTrashModal(false)}>×</button>
            <h2>Trash</h2>
            <div className="trash-actions-top">
              <button
                className="restore-all-btn"
                onClick={handleRestoreAll}
                disabled={queuedForDeletion.length === 0 || isProcessingDeletionQueue}
              >
                Restore All
              </button>
              <button
                className="empty-trash-btn"
                onClick={handleEmptyTrash}
                disabled={queuedForDeletion.length === 0 || isProcessingDeletionQueue}
              >
                {isProcessingDeletionQueue ? 'Emptying...' : 'Empty Trash'}
              </button>
            </div>

            {imagesInTrash.length === 0 ? (
              <p className="no-items-message">Trash is empty.</p>
            ) : (
              <div className="trash-gallery-grid">
                {imagesInTrash.map((img) => (
                  <div key={img._id} className="trash-card">
                    <div className="trash-image-preview">
                      <img src={img.url} alt={img.name} />
                    </div>
                    <div className="trash-card-info">
                      <h4 className="trash-image-title">{img.name}</h4>
                      <div className="trash-card-actions">
                        <button
                          className="restore-btn"
                          onClick={() => handleRestoreImage(img._id)}
                          disabled={isProcessingDeletionQueue}
                        >
                          Restore
                        </button>
                        <button
                          className="delete-permanently-btn"
                          onClick={() => handlePermanentlyDeleteImage(img._id)}
                          disabled={isProcessingDeletionQueue}
                        >
                          Delete Permanently
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Image Full-Screen Preview Modal */}
      {previewImage && (
        <div className="image-preview-overlay" onClick={() => {
          setPreviewImage(null);
          setPreviewImageIndex(null);
          setShowOptionsDropdown(false);
        }}>
          <div className="preview-box" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => {
              setPreviewImage(null);
              setPreviewImageIndex(null);
              setShowOptionsDropdown(false);
            }}>×</button>

            {filteredImages.length > 1 && (
              <>
                <button className="nav-button prev-button" onClick={() => navigateImage(-1)}>&#10094;</button>
                <button className="nav-button next-button" onClick={() => navigateImage(1)}>&#10095;</button>
              </>
            )}

            <img src={previewImage.url} alt={previewImage.name} />

            <div className="options-container" ref={optionsDropdownRef}>
              <button className="options-button" onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}>
                <MoreHorizIcon />
              </button>
              {showOptionsDropdown && (
                <div className="options-dropdown">
                  <button onClick={handleEditFromPreview} disabled={isProcessingDeletionQueue} title="Edit Image">
                    <EditIcon /> Edit
                  </button>
                  <button onClick={handleDeleteFromPreview} disabled={isProcessingDeletionQueue} title="Delete Image">
                    <DeleteIcon /> Delete
                  </button>
                  <button onClick={() => copyToClipboard(previewImage.url)} disabled={isProcessingDeletionQueue} title="Copy URL">
                    <ContentCopyIcon /> Copy
                  </button>
                  {/* Preview Download Button: Use onClick directly with combined disabled state */}
                  <button
                    onClick={(e) => handleDownload(e, previewImage.url, previewImage.name + '.png')}
                    disabled={isProcessingDeletionQueue || isDownloadDisabled}
                    title="Download Image"
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>
              )}
            </div>

            <div className="preview-image-info">
              <h3>{previewImage.name}</h3>
              <p>{previewImage.description}</p>
              {previewImage.tags && previewImage.tags.length > 0 && (
                <p>Tags: {previewImage.tags.filter(Boolean).join(', ')}</p>
              )}
              {previewImage.imageCategories && previewImage.imageCategories.length > 0 && (
                <p>Categories: {previewImage.imageCategories.map(cat => cat.name).join(', ')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Indicator */}
      {uploadingTotal > 0 && (
        <div className="upload-progress">
          <div className="upload-text">
            Uploading {uploadingIndex} / {uploadingTotal}
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        </div>
      )}

      {/* NEW: Loading Spinner Modal */}
      {isLoading && (
        <div className="modal-overlay loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing...</p>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Images;